import { css } from '@emotion/css';
import Button, { Variations as ButtonVariations } from 'components/Button';
import GiftReminderItem, { ReminderListItem } from './GiftReminderItem';
import { make as PaginationContainer } from 'components/library/PaginationContainer.bs';
import React from 'react';
import spacetime from 'spacetime';

import GiftReminderWrapper from './GiftReminderWrapper';
import { Reminder } from './types';
import { useRemindersContext } from './context';
import { ModalState } from 'pages/reminders/reminders';
import { generateUniqueReminderId } from 'utils/reminder';

const rightWrapper = css({
  flex: 1,
});

const buttonWrapper = css({
  width: '160px',
  '@media (min-width: 768px)': {
    width: '190px',
  },
  float: 'right',
});
const wrapper = css({
  display: 'flex',
});

const innerWrapper = css({
  width: '100%',
  '@media (min-width: 1280px)': {
    width: '55%',
  },
});
const paginationContainer = css({
  maxWidth: '23.0->rem',
});
const footer = css({
  marginTop: '1.5rem',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  '@media (max-width: 640px)': {
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

const footerText = css({
  '@media (max-width: 640px)': {
    display: 'none',
  },
});

const monthTitle = css({
  fontSize: '18px',
  lineHeight: '21px',
  paddingBottom: '8px',
  paddingTop: '24px',
  fontWeight: 500,
});

export const toReminderList = results =>
  results.reduce((carry, item) => {
    const itemMonth = spacetime(item.nextOccurrenceDate).format('{month} {year}');
    const existing = carry[itemMonth];
    carry[itemMonth] = [...(existing || []), item];
    return carry;
  }, {});

export interface ReminderList {
  [key: string]: Array<ReminderListItem>;
}

interface Props {
  count: number;
  currentPage: number;
  pageSize: number;
  results: Reminder[];
}

const GiftReminderList = ({ count, currentPage, pageSize, results }: Props) => {
  const totalReminders = results.length;
  const fromCount = (currentPage - 1) * pageSize + 1;
  const toCount = (currentPage - 1) * pageSize + totalReminders;
  const [menuId, setMenuId] = React.useState(null);
  const menuRef = React.useRef<HTMLElement>();
  const resultsGrouped = toReminderList(results);
  const { setModalState, setReminderInstanceId } = useRemindersContext();
  const handleClosure = React.useCallback(
    event => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !!menuId &&
        !event.target.classList.contains('menu-btn')
      ) {
        setMenuId(null);
      }
    },
    [menuId]
  );

  React.useEffect(() => {
    window.addEventListener('click', handleClosure);

    return () => {
      window.removeEventListener('click', handleClosure);
    };
  }, [handleClosure]);

  return (
    <GiftReminderWrapper
      headerRight={
        <div className={rightWrapper}>
          <div className={buttonWrapper}>
            <Button
              className="create-reminder-button"
              type="button"
              variation={ButtonVariations.Primary}
              onClick={() => {
                setReminderInstanceId(generateUniqueReminderId());
                setModalState({ state: ModalState.New });
              }}
            >
              Create a reminder
            </Button>
          </div>
        </div>
      }
    >
      <div className={wrapper}>
        <div className={innerWrapper}>
          {Object.keys(resultsGrouped).map(key => (
            <React.Fragment key={key}>
              <p className={monthTitle}>{key}</p>
              {resultsGrouped[key].map(item => (
                <GiftReminderItem
                  menuRef={menuRef}
                  showMenu={menuId === item.uid}
                  setShowMenu={id => setMenuId(id)}
                  item={item}
                  key={item.uid}
                />
              ))}
            </React.Fragment>
          ))}
          <div className={footer}>
            <div className={footerText}>
              Showing{' '}
              <b>
                {fromCount}-{toCount}
              </b>{' '}
              out of {count} reminders
            </div>
            <PaginationContainer
              {...{
                count,
                currentPage,
                pageSize,
                className: paginationContainer,
                urlConstructor: pageNum => `/reminders/?page=${pageNum}`,
              }}
            />
          </div>
        </div>
      </div>
    </GiftReminderWrapper>
  );
};

export default React.memo(GiftReminderList);
